import { useState } from 'react';
import { useSelector } from 'react-redux';

import Button from '@components/button/Button';
import { ReportDateRangePicker } from '@components/index';
import { LOCAL_STORAGE_KEYS } from '@constants/storage';
import { INPUT_FOCUS_CLASSNAME } from '@features/lead-management/constants/common';
import { isAllOptionsSelected } from '@features/lead-management/utils/common';
import { isSalesAgent as isSalesAgentScope } from '@helpers/roleScope';
import { DateRange as UIDateRange } from '@types';
import { getLocalStorageItem } from '@utils/storage';
import { DropdownOption } from 'types/dropdown';

import AgentSelector from '../AgentSelector';
import { leadDateRangeOptions } from './helper';
import { AgentReportAnalysisProps } from './types';

const AgentReportAnalysis: React.FC<AgentReportAnalysisProps> = ({
  formData,
  onFormChange,
  onDownloadReport,
  isFormValid,
  isGenerating = false,
  handleAllOptionsSelection
}) => {
  const organizationId = getLocalStorageItem(
    LOCAL_STORAGE_KEYS.ORGANIZATION_ID
  );
  const { rolesAndPermissions, currentUser } = useSelector(
    (state: any) => state.user
  );
  const isSalesAgent = isSalesAgentScope(rolesAndPermissions);
  const [currentOption, setCurrentOption] = useState<string>('');

  const handleDateRangeSelect = (dateRange: UIDateRange) => {
    onFormChange('startDate', dateRange.startDate);
    const endDateWithTime = new Date(dateRange.endDate);
    endDateWithTime.setHours(23, 59, 59, 999);
    onFormChange('endDate', endDateWithTime);
  };

  const handleMultiSelectChange = (
    field: string,
    selectedOptions: DropdownOption[],
    options: DropdownOption[]
  ) => {
    const isAllSelected = isAllOptionsSelected(selectedOptions, options);
    handleAllOptionsSelection(field, isAllSelected);
    onFormChange(field, selectedOptions);
  };

  return (
    <>
      <div className="flex flex-wrap gap-6">
        <div className="w-full md:w-[calc(33.333%-1rem)]">
          <label className="mb-1 block text-sm font-semibold text-darkText">
            Lead Creation Date <span className="text-red-500">*</span>
          </label>
          <ReportDateRangePicker
            currentFilter={currentOption}
            filterOptions={leadDateRangeOptions}
            onSelectFilter={(dateRange, option) => {
              handleDateRangeSelect(dateRange);
              setCurrentOption(option);
            }}
            customClass="w-full"
            customDropdownClass="min-h-[610px]"
            buttonClassName={`!bg-theme ${INPUT_FOCUS_CLASSNAME}`}
          />
        </div>

        <div className="w-full md:w-[calc(33.333%-1rem)]">
          {isSalesAgent ? (
            <>
              <label className="mb-2 block text-sm font-semibold text-darkText">
                Assigned Agent/Staff
              </label>
              <div className="flex h-12.5 items-center rounded-xl border border-grey40 !bg-theme px-4">
                <div className="flex h-7 w-fit items-center rounded-2xl !bg-grey10 px-2.5 py-[5px] text-sm font-normal text-cofeeNeutral10">
                  {currentUser?.name}
                </div>
              </div>
            </>
          ) : (
            <AgentSelector
              selectedAgents={formData.assignedAgent}
              setSelectedAgents={(agents, options) =>
                handleMultiSelectChange('assignedAgent', agents, options)
              }
              organizationId={organizationId}
            />
          )}
        </div>
      </div>

      <div className="pt-6">
        <Button
          variant="primary"
          size="large"
          label="Download Report"
          handleClick={onDownloadReport}
          disabled={!isFormValid}
          loading={isGenerating}
          className="w-full px-8 py-3 md:w-auto"
        />
      </div>
    </>
  );
};

export default AgentReportAnalysis;
